import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PlayCircle, Loader2, ArrowLeft, Lock, CheckCircle2, BookOpenText } from 'lucide-react';
import { toast } from 'sonner';
import { ShareLesson } from '../../components/courses/ShareLesson';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { fetchOwnedCourse, fetchOwnedCourseLessons } from '../../features/courses/api/courses';
import type { Course, Lesson } from '../../types';
import { COURSE } from '../../constants/testIds';

export default function OwnedCourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!courseId) return;
    Promise.all([fetchOwnedCourse(courseId), fetchOwnedCourseLessons(courseId)])
      .then(([c, l]) => {
        setCourse(c);
        setLessons(l);
      })
      .catch((e) => {
        if (e.response?.status === 403) setDenied(true);
      })
      .finally(() => setLoading(false));
  }, [courseId]);

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Course Detail">
      <div className="max-w-4xl">
        <Link to="/member/courses" className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-400 mb-6 text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Course Saya
        </Link>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
          </div>
        ) : denied ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-10 text-center">
            <Lock className="w-10 h-10 text-white/30 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-white/60">Anda belum memiliki akses ke course ini.</p>
          </div>
        ) : course ? (
          <div data-testid={COURSE.ownedDetail}>
            <div className="bg-gradient-to-br from-cyan-400/10 to-transparent border border-cyan-400/20 rounded-2xl p-8 mb-6">
              <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-400">{course.category}</span>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white mt-2">{course.title}</h2>
              <p className="text-white/60 mt-2 text-sm">{course.description}</p>
            </div>

            <h3 className="text-lg font-medium text-white mb-4">Materi Lesson</h3>
            {lessons.length === 0 ? (
              <p className="text-white/50">Belum ada lesson yang dipublikasikan.</p>
            ) : (
              <div className="space-y-3">
                {lessons.map((l, idx) => {
                  const locked = !!l.locked;
                  const completed = !!l.completed;
                  return (
                    <div key={l.id} className="flex items-center gap-2">
                    <button
                      data-testid={COURSE.lessonItem}
                      disabled={locked}
                      onClick={() => {
                        if (locked) {
                          toast.info('Terkunci', { description: 'Selesaikan lesson sebelumnya dulu untuk membuka materi ini.' });
                          return;
                        }
                        navigate(`/member/courses/${courseId}/lessons/${l.id}`);
                      }}
                      className={`w-full flex items-center gap-4 border rounded-xl p-4 text-left transition-colors ${
                        locked
                          ? 'bg-white/[0.01] border-white/5 opacity-60 cursor-not-allowed'
                          : 'bg-white/[0.02] border-white/10 hover:border-cyan-400/30 hover:bg-white/[0.04]'
                      }`}
                    >
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-medium shrink-0 border ${
                          completed
                            ? 'bg-emerald-400/10 border-emerald-400/30 text-emerald-400'
                            : 'bg-cyan-400/10 border-cyan-400/30 text-cyan-400'
                        }`}
                      >
                        {completed ? <CheckCircle2 className="w-5 h-5" strokeWidth={2} /> : idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium truncate">{l.title}</p>
                          {completed && (
                            <span className="text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full shrink-0">
                              Selesai
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white/50 line-clamp-1">{l.description}</p>
                      </div>
                      {locked ? (
                        <Lock className="w-5 h-5 text-white/30 shrink-0" strokeWidth={1.5} />
                      ) : l.content_type === 'pdf' ? (
                        <BookOpenText className="w-5 h-5 text-cyan-400 shrink-0" strokeWidth={1.5} />
                      ) : (
                        <PlayCircle className="w-5 h-5 text-cyan-400 shrink-0" strokeWidth={1.5} />
                      )}
                    </button>
                    {!locked && <ShareLesson courseId={courseId!} lessonId={l.id} title={l.title} compact />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="text-white/50 py-20 text-center">Course tidak ditemukan.</div>
        )}
      </div>
    </DashboardLayout>
  );
}
